import { useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrVorlage } from './typen';
import { ART, GEWERK } from './gemeinsam';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection,
  StatusBadge, type Spalte,
} from '../../muster';

/**
 * Datenpunktvorlagen — gruppiert nach Gewerk → Bauteilart (Vertrag Abschnitt
 * 4.1). Globale Vorlagen (`projektbezogen=false`) tragen die Marke „global".
 *
 * Umzug U6c: Liste (`DataTable`) und Bearbeitung (`Drawer` mit
 * `FormSection`/`Feld`) getrennt. Die elf Felder standen bisher als eine
 * Zeile in der Tabelle; die Warnung „diese Vorlage ist global" war dabei
 * erst sichtbar, NACHDEM man auf Bearbeiten geklickt hatte — sie steht
 * jetzt im Kopf des Schubfachs.
 */
export function Datenpunktvorlagen({ api, projektId, vorlagen, onGeaendert }: {
  api: ApiClient; projektId: string; vorlagen: MsrVorlage[]; onGeaendert: () => void;
}) {
  const [entwurf, setEntwurf] = useState<MsrVorlage | null>(null);
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const gruppen = useMemo(() => {
    const m = new Map<string, MsrVorlage[]>();
    for (const v of vorlagen) {
      const schluessel = `${v.gewerk}|${v.bauteilart}`;
      const liste = m.get(schluessel) ?? [];
      liste.push(v);
      m.set(schluessel, liste);
    }
    return Array.from(m.entries());
  }, [vorlagen]);

  const bearbeiten = (v: MsrVorlage) => { setEntwurf({ ...v }); setFehler(null); };
  const aendern = (teil: Partial<MsrVorlage>) =>
    setEntwurf((v) => (v ? { ...v, ...teil } : v));

  const speichern = async () => {
    if (!entwurf) return;
    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      const { id, ...rest } = entwurf;
      await api.msrVorlageSetzen(projektId, id, rest);
      setEntwurf(null);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const spalten: Spalte<MsrVorlage>[] = [
    { schluessel: 'funktion', titel: 'Funktion', wert: (v) => v.funktion, immerSichtbar: true },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (v) => v.bezeichnung },
    { schluessel: 'art', titel: 'Art', wert: (v) => ART[v.art] },
    { schluessel: 'signal', titel: 'Signal', wert: (v) => v.signal ?? null },
    { schluessel: 'anbindung', titel: 'Anbindung', wert: (v) => v.anbindung },
    { schluessel: 'klemmen', titel: 'Klemmen', art: 'zahl', wert: (v) => v.klemmen },
    { schluessel: 'pflicht', titel: 'Pflicht', breite: '90px',
      wert: (v) => (v.pflicht ? 'ja' : 'nein') },
    { schluessel: 'bedingung', titel: 'Bedingung', wert: (v) => v.bedingung ?? null },
    { schluessel: 'norm', titel: 'Norm', wert: (v) => v.norm ?? null },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (v) => v.begruendung ?? null },
    {
      schluessel: 'istAktiv', titel: 'Zustand', art: 'status', breite: '120px',
      wert: (v) => (v.istAktiv ? 'aktiv' : 'stillgelegt'),
      inhalt: (v) => (v.istAktiv
        ? <StatusBadge klein zustand="gut" text="aktiv" />
        : <StatusBadge klein zustand="neutral" text="stillgelegt" />),
    },
    {
      schluessel: 'geltung', titel: 'Geltung', breite: '170px',
      wert: (v) => (v.projektbezogen ? 'Projekt' : 'global'),
      inhalt: (v) => (
        <>
          {!v.projektbezogen && (
            <StatusBadge klein zustand="info" text="global"
              titel="Gilt für alle Projekte." />
          )}
          <button type="button" className="m-knopf" style={{ marginLeft: 6 }}
            onClick={() => bearbeiten(v)}>Bearbeiten</button>
        </>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Datenpunktvorlagen"
      beschreibung="Je Gewerk und Bauteilart die Datenpunkte, die die Ableitung anlegt."
      meta={`${gruppen.length} Gruppe(n) · ${vorlagen.length} Vorlagen`}
    >
      {gruppen.map(([schluessel, liste]) => {
        const [gewerk, bauteilart] = schluessel.split('|');
        return (
          <DataTable
            key={schluessel}
            name={`${(GEWERK as Record<string, string>)[gewerk] ?? gewerk} — ${bauteilart}`}
            spalten={spalten}
            zeilen={liste}
            schluessel={(v) => v.id}
            sortierung={{ spalte: 'funktion', richtung: 'auf' }}
            dicht
            suche={false}
            spaltenwahl={false}
            aktiveZeile={entwurf?.id}
            zeilenKlasse={(v) => (v.istAktiv ? undefined : 'schwach')}
            kontextmenue={[{ beschriftung: 'Bearbeiten …', beiWahl: bearbeiten }]}
            leer={<EmptyState klein titel="Für diese Gruppe ist keine Vorlage hinterlegt." />}
          />
        );
      })}

      {vorlagen.length === 0 && (
        <EmptyState
          titel="Für dieses Projekt ist keine Datenpunktvorlage hinterlegt."
          erklaerung={'Ohne Vorlage legt der Ableitungslauf keinen Datenpunkt an — die '
            + 'Vorlagen kommen aus dem globalen Katalog oder aus der Projektvorlage.'}
        />
      )}

      <Drawer
        offen={entwurf !== null}
        titel={entwurf ? `${entwurf.funktion} — ${entwurf.bezeichnung}` : ''}
        beschreibung={entwurf
          ? `${(GEWERK as Record<string, string>)[entwurf.gewerk] ?? entwurf.gewerk} · `
            + `${entwurf.bauteilart}`
          : undefined}
        beiSchliessen={() => setEntwurf(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt" disabled={speichertLaeuft}
              onClick={() => void speichern()}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
            <button type="button" className="m-knopf" onClick={() => setEntwurf(null)}>
              Abbrechen
            </button>
          </>
        )}
      >
        {entwurf && !entwurf.projektbezogen && (
          <p className="hinweis warnung" style={{ marginTop: 0 }}>
            Diese Vorlage ist global — die Änderung wirkt für alle Projekte.
          </p>
        )}

        {entwurf && (
          <FormSection titel="Vorlage" spalten={2}>
            <Feld beschriftung="Funktion" wert={entwurf.funktion} pflicht
              beiAenderung={(w) => aendern({ funktion: w as string })} />
            <Feld beschriftung="Bezeichnung" wert={entwurf.bezeichnung} pflicht
              beiAenderung={(w) => aendern({ bezeichnung: w as string })} />
            <Feld art="auswahl" beschriftung="Datenpunktart" wert={entwurf.art}
              optionen={Object.entries(ART).map(([k, n]) => ({ wert: k, beschriftung: n }))}
              beiAenderung={(w) => aendern({ art: w as MsrVorlage['art'] })} />
            <Feld beschriftung="Signal" wert={entwurf.signal ?? ''}
              beiAenderung={(w) => aendern({ signal: w as string })} />
            <Feld art="auswahl" beschriftung="Anbindung" wert={entwurf.anbindung}
              optionen={[
                { wert: 'klemme', beschriftung: 'Klemme' },
                { wert: 'bus', beschriftung: 'Bus' },
              ]}
              beiAenderung={(w) => aendern({ anbindung: w as 'klemme' | 'bus' })} />
            <Feld art="zahl" beschriftung="Klemmen" wert={entwurf.klemmen} min={0}
              beiAenderung={(w) => aendern({ klemmen: (w as number | null) ?? 0 })} />
            <Feld beschriftung="Bedingung" wert={entwurf.bedingung ?? ''}
              hinweis="Leer heißt: gilt immer."
              beiAenderung={(w) => aendern({ bedingung: w as string })} />
            <Feld beschriftung="Norm" wert={entwurf.norm ?? ''}
              beiAenderung={(w) => aendern({ norm: w as string })} />
            <Feld art="haken" beschriftung="Pflichtdatenpunkt" wert={entwurf.pflicht}
              beiAenderung={(w) => aendern({ pflicht: w as boolean })} />
            <Feld art="schalter" beschriftung="Vorlage aktiv" wert={entwurf.istAktiv}
              beiAenderung={(w) => aendern({ istAktiv: w as boolean })} />
            <Feld beschriftung="Begründung" wert={entwurf.begruendung ?? ''} breite="voll"
              beiAenderung={(w) => aendern({ begruendung: w as string })} />
          </FormSection>
        )}

        {fehler && (
          <ErrorState
            klein
            meldung="Die Datenpunktvorlage konnte nicht gespeichert werden."
            technisch={fehler}
            beiErneutVersuchen={() => void speichern()}
          />
        )}
      </Drawer>
    </ContentSection>
  );
}
